/**
 * Secure Token Storage Utility
 *
 * Security Strategy:
 * 1. Access Token: Stored in memory only (not in localStorage/sessionStorage)
 * 2. Encrypted Token: Stored in sessionStorage (cleared on tab close)
 * 3. Auto-restore: On page load, can restore from sessionStorage if needed
 */

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'ai_interview_access_token',
  ENCRYPTED_TOKEN: 'ai_interview_encrypted_token',
  SESSION_ID: 'ai_interview_session',
  USER_DATA: 'ai_interview_user',
};

class TokenStorage {
  constructor() {
    // In-memory storage for access token (most secure)
    this.accessToken = null;
  }

  /**
   * Store both tokens securely
   * @param {string} accessToken - JWT access token
   * @param {string} encryptedToken - AES encrypted token
   */
  setTokens(accessToken, encryptedToken) {
    // Store access token in memory and sessionStorage (for page refresh persistence)
    this.accessToken = accessToken;
    if (accessToken) {
      sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    }

    // Store encrypted token in sessionStorage (cleared on tab close)
    if (encryptedToken) {
      sessionStorage.setItem(STORAGE_KEYS.ENCRYPTED_TOKEN, encryptedToken);
    }
  }

  /**
   * Get the access token from memory or sessionStorage
   * @returns {string|null}
   */
  getAccessToken() {
    // Restore from sessionStorage if not in memory (handles page refresh)
    if (!this.accessToken) {
      this.accessToken = sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    }
    return this.accessToken;
  }

  /**
   * Get the encrypted token from sessionStorage
   * @returns {string|null}
   */
  getEncryptedToken() {
    return sessionStorage.getItem(STORAGE_KEYS.ENCRYPTED_TOKEN);
  }

  /**
   * Check if user has valid tokens
   * @returns {boolean}
   */
  hasValidTokens() {
    return !!(this.accessToken || this.getEncryptedToken());
  }

  /**
   * Clear all tokens (logout)
   */
  clearTokens() {
    this.accessToken = null;
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.ENCRYPTED_TOKEN);
  }

  /**
   * Store user session data
   * @param {object} data - User session data
   */
  setSessionData(data) {
    if (data) {
      sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, data.sessionId || '');
      sessionStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(data.user || {}));
    }
  }

  /**
   * Get stored session data
   * @returns {object|null}
   */
  getSessionData() {
    try {
      const sessionId = sessionStorage.getItem(STORAGE_KEYS.SESSION_ID);
      const userData = sessionStorage.getItem(STORAGE_KEYS.USER_DATA);

      if (!sessionId || !userData) return null;

      return {
        sessionId,
        user: JSON.parse(userData),
      };
    } catch (error) {
      console.error('Error reading session data:', error);
      return null;
    }
  }

  /**
   * Clear all session data
   */
  clearSessionData() {
    sessionStorage.removeItem(STORAGE_KEYS.SESSION_ID);
    sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  /**
   * Clear everything (logout)
   */
  clearAll() {
    this.clearTokens();
    this.clearSessionData();
  }

  /**
   * Restore access token from encrypted token if available
   * This would typically involve calling a backend endpoint to exchange
   * the encrypted token for a new access token
   *
   * For now, returns the encrypted token presence
   * @returns {boolean}
   */
  canRestoreSession() {
    return !!this.getEncryptedToken();
  }
}

// Create singleton instance
const tokenStorage = new TokenStorage();

export default tokenStorage;

// Export for testing
export { TokenStorage, STORAGE_KEYS };
